/**
 * AI Parameter Slider Component
 * 
 * Allows users to adjust AI generation parameters (temperature, etc.)
 * For creative/professional personas only - regulated personas show locked state.
 */

import React, { useState } from 'react';
import { Button, Label } from '@/components/ui';

// Inline SVG icons to avoid external dependency
const AlertTriangleIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-4 h-4"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

const RotateCcwIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-4 h-4"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const LockIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-5 h-5"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const UnlockIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-4 h-4"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
  </svg>
);

interface AIParameterSliderProps {
  // Persona settings
  industryClassification: 'regulated' | 'professional' | 'creative';
  temperatureLocked: boolean;
  defaultTemperature: number;
  minTemperature?: number;
  maxTemperature?: number;
  
  // Current values
  currentTemperature: number;
  
  // Callbacks
  onTemperatureChange: (value: number) => void;
  onReset: () => void;
  
  // UI options
  personaName?: string;
  showComplianceWarning?: boolean;
}

/**
 * Slider with labeled endpoints
 */
function Slider({
  value,
  min,
  max,
  onChange,
  disabled,
  leftLabel,
  rightLabel,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  leftLabel: string;
  rightLabel: string;
}) {
  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <div className="space-y-1">
      <div className="relative h-2">
        {/* Track background */}
        <div className="absolute inset-0 rounded-full bg-muted" />
        
        {/* Filled track */}
        <div 
          className={`absolute inset-y-0 left-0 rounded-full transition-all ${
            disabled ? 'bg-disabled' : 'bg-info'
          }`}
          // eslint-disable-next-line no-restricted-syntax -- Dynamic width for slider
          style={{ width: `${percentage}%` }}
        />
        
        {/* Slider input - Using raw input for range slider functionality */}
        {/* eslint-disable-next-line no-restricted-syntax -- Range slider requires native input element */}
        <input
          type="range"
          min={min * 100}
          max={max * 100}
          value={value * 100}
          onChange={e => onChange(Number(e.target.value) / 100)}
          disabled={disabled}
          className={`absolute inset-0 w-full h-full opacity-0 cursor-pointer ${
            disabled ? 'cursor-not-allowed' : ''
          }`}
        />
        
        {/* Thumb indicator */}
        <div 
          className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full shadow-md transition-all ${
            disabled 
              ? 'bg-disabled border-2 border-border' 
              : 'bg-card border-2 border-primary'
          }`}
          // eslint-disable-next-line no-restricted-syntax -- Dynamic left position for slider thumb
          style={{ left: `calc(${percentage}% - 8px)` }}
        />
      </div>
      
      {/* Labels */}
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{leftLabel}</span>
        <span className="font-mono">{value.toFixed(2)}</span>
        <span>{rightLabel}</span>
      </div>
    </div>
  );
}

/**
 * AI Parameter Slider Panel
 */
export function AIParameterSlider({
  industryClassification,
  temperatureLocked,
  defaultTemperature,
  minTemperature = 0.1,
  maxTemperature = 0.95,
  currentTemperature,
  onTemperatureChange,
  onReset,
  personaName,
  showComplianceWarning = true,
}: AIParameterSliderProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // For regulated industries, show locked state
  if (temperatureLocked || industryClassification === 'regulated') {
    return (
      <div className="rounded-lg border border-warning bg-warning-light p-4">
        <div className="flex items-start gap-3">
          <LockIcon className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-warning">
              AI Settings ({personaName || 'Regulated Persona'})
            </h3>
            <p className="text-sm text-warning mt-1">
              Temperature controls disabled for compliance
            </p>
            <ul className="text-xs text-warning mt-2 space-y-1">
              <li>• Optimized for deterministic, auditable outputs</li>
              <li>• Required for certification compliance</li>
              <li>• Temperature: {defaultTemperature.toFixed(2)} (locked)</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // For professional/creative, show adjustable sliders
  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      {/* Header */}
      <Button
        onClick={() => setIsExpanded(!isExpanded)}
        variant="ghost"
        className="w-full flex items-center justify-between p-3 h-auto rounded-none"
      >
        <div className="flex items-center gap-2">
          <UnlockIcon className="w-4 h-4 text-link" />
          <span className="font-medium text-foreground">
            Writing Style
          </span>
          <span className="text-xs text-muted-foreground">
            ({personaName || industryClassification})
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground font-mono">
            {currentTemperature.toFixed(2)}
          </span>
          <svg
            className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </Button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="p-4 pt-0 space-y-4 border-t border-border">
          {/* Creativity slider */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-foreground">
              Creativity
            </Label>
            <Slider
              value={currentTemperature}
              min={minTemperature}
              max={maxTemperature}
              onChange={onTemperatureChange}
              leftLabel="← Precise"
              rightLabel="Creative →"
            />
            <p className="text-xs text-muted-foreground">
              {currentTemperature < 0.3 
                ? 'Very consistent, deterministic responses'
                : currentTemperature < 0.5
                ? 'Balanced precision with some variation'
                : currentTemperature < 0.7
                ? 'More creative and varied responses'
                : 'Highly creative, experimental outputs'
              }
            </p>
          </div>

          {/* Professional range warning */}
          {industryClassification === 'professional' && showComplianceWarning && (
            <div className="flex items-start gap-2 text-xs text-warning bg-warning-light p-2 rounded">
              <AlertTriangleIcon className="w-4 h-4 flex-shrink-0" />
              <span>
                Temperature range limited to {minTemperature.toFixed(1)}-{maxTemperature.toFixed(1)} for professional standards.
              </span>
            </div>
          )}

          {/* Reset button */}
          <Button
            onClick={onReset}
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-link-hover"
          >
            <RotateCcwIcon className="w-4 h-4" />
            Reset to Persona Defaults
          </Button>
        </div>
      )}
    </div>
  );
}

export default AIParameterSlider;

